import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, notify, isTaskOverdue, canClientUpload, canStaffUploadForClient, clientHasRejectedDocs, clientHasRequiredUploadTasks, shouldPersistAudit } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { InvoicesTable } from '../invoices/InvoicesPage';
import { IconProperties } from '../../icons.properties';
import { listClientDocs, uploadClientDoc } from '../../storage';
import { DocActions } from '../../components/DocActions';
import { TasksTable } from '../tasks/TasksPage';
import { Icon, InlineIcon } from '../../components/Icon';



function ClientDetail({ client, onBack, canEdit = true }) {
  const { tasks, invoices, employees, openModal, showToast, submissions, setClients, can } = useApp();
  const [tab, setTab] = useState("overview");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [submittingDelete, setSubmittingDelete] = useState(false);
  const clientTasks    = tasks.filter(t => t.clientId === client.id);
  const clientInvoices = invoices.filter(i => i.clientId === client.id);
  const emp            = employees.find(e => e.id === client.assignedTo);
  const salesEmp       = employees.find(e => e.id === client.salesOwner);

  const submitDeleteRequest = async () => {
    setSubmittingDelete(true);
    try {
      await sb.createDeleteRequest({
        entity_type: "clients",
        entity_id: client.id,
        reason: deleteReason.trim(),
        company_id: client.id,
        reassign_to: null,
      });
      showToast("Delete request submitted — awaiting admin approval", "success");
      setConfirmDelete(false);
      setDeleteReason("");
      onBack();
    } catch (err) {
      showToast(err?.message || "Failed to submit delete request", "error");
    } finally {
      setSubmittingDelete(false);
    }
  };

  return (
    <>
      <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
        <button className="btn" onClick={onBack}>← All Clients</button>
        {!canEdit && (
          <div style={{ padding:"5px 12px",background:"#FFF8EC",border:"1px solid rgba(201,161,74,.4)",borderRadius:6,fontSize:12,color:"var(--gold-dk)",fontWeight:600 }}>
            <InlineIcon name="view" size={14} gap={5}>View Only — Assigned to another team member</InlineIcon>
          </div>
        )}
        {canEdit && (
          <button className="btn btn-sm" style={{ marginLeft:"auto",background:"var(--navy)",color:"#fff",border:"none" }}
            onClick={()=>openModal("edit-client",{clientId:client.id})}>
            <InlineIcon name="edit" size={14} gap={5}>Edit Client</InlineIcon>
          </button>
        )}
        {can("clients","delete") && !confirmDelete && (
          <button className="btn btn-sm" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5"}}
            onClick={()=>{ setConfirmDelete(true); setDeleteReason(""); }}>
            <InlineIcon name="trash" size={14} gap={5}>Delete Client</InlineIcon>
          </button>
        )}
      </div>
      {confirmDelete && (
        <div className="dr-confirm-form card" style={{ padding:"16px 20px", background:"#FEF2F2", border:"1px solid #FCA5A5", marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:6 }}>Delete "{client.name}"?</div>
          <div style={{ fontSize:12, color:"#991B1B", marginBottom:4 }}>This requires admin approval before taking effect.</div>
          <div style={{ fontSize:11, color:"#7F1D1D", marginBottom:10 }}>Deleting this client will hide them from lists. Open invoices and tasks will remain.</div>
          <div className="f-group" style={{ marginBottom:10 }}>
            <label className="f-label">Reason for deletion (optional)</label>
            <textarea className="f-textarea" rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} placeholder="Add a reason for this deletion request…" />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-sm" onClick={()=>{ setConfirmDelete(false); setDeleteReason(""); }} disabled={submittingDelete}>Cancel</button>
            <button className="btn btn-sm btn-red" onClick={submitDeleteRequest} disabled={submittingDelete}>
              <InlineIcon name="trash" size={13} gap={5}>{submittingDelete ? "Submitting…" : "Submit Delete Request"}</InlineIcon>
            </button>
          </div>
        </div>
      )}

      {/* Client hero */}
      <div className="card" style={{ padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 13, background: "linear-gradient(135deg,var(--navy),var(--blue))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}><Icon name="company" size={22} color="#fff" /></div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="sr" style={{ fontSize: 20 }}>{client.name}</span>
              <span className="tag">{client.type}</span>
              <Badge status={client.status === "active" ? "completed" : "open"} label={client.status === "active" ? "Active" : "Pending"} />
            </div>
            <div style={{ display: "flex", gap: 20, marginTop: 8, flexWrap: "wrap", fontSize: 12, color: "var(--muted)" }}>
              <InlineIcon name="clipboard" size={13} gap={5}>{client.clientNo}</InlineIcon>
              <InlineIcon name="phoneCall" size={13} gap={5}>{client.phone}</InlineIcon>
              <InlineIcon name="mail" size={13} gap={5}>{client.email}</InlineIcon>
              <InlineIcon name="calendar" size={13} gap={5}>Since {client.createdAt}</InlineIcon>
              {emp && <InlineIcon name="user" size={13} gap={5}>Execution: {emp.name}</InlineIcon>}
              {salesEmp && <InlineIcon name="briefcase" size={13} gap={5}>Sales: {salesEmp.name}</InlineIcon>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={() => openModal("create-invoice", { clientId: client.id, clientName: client.name })}>+ Invoice</button>
          </div>
        </div>
      </div>

      <div className="tabs">
        {["overview", "tasks", "invoices", "documents", "audit"].map(t => (
          <div key={t} className={`tab ${tab === t ? "on" : ""}`} onClick={() => setTab(t)} style={{ textTransform: "capitalize" }}>{t === "audit" ? "Errors" : t}</div>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid2">
          <div className="card">
            <div className="card-head"><div className="card-title">Payment Summary</div></div>
            <div className="card-body">
              {(()=>{const live=clientInvoices.filter(i=>i.status!=="void");const tb=live.reduce((s,i)=>s+(i.total||0),0);const tc=live.reduce((s,i)=>s+(i.paid||0),0);const tp=live.reduce((s,i)=>s+(i.pending||0),0);return [["Total Billed",INR(tb),"var(--ink)"],["Collected",INR(tc),"var(--green)"],["Outstanding",INR(tp),tp>0?"var(--red)":"var(--muted)"]];})().map(([l, v, c]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>{l}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</span>
                </div>
              ))}
              <div className="prog-bg" style={{ marginTop: 12 }}>
                <div className="prog-fill" style={{ width: client.progress + "%", background: client.progress === 100 ? "var(--green)" : "var(--blue)" }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{client.progress}% complete</div>
            </div>
          </div>
          <div className="card">
            <div className="card-head"><div className="card-title">Task Summary</div></div>
            <div className="card-body">
              {[["Total", clientTasks.length, "var(--ink)"], ["Completed", clientTasks.filter(t => t.status === "completed").length, "var(--green)"], ["Pending", clientTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length, "var(--orange)"]].map(([l, v, c]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>{l}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "tasks" && (
        <>

          <TasksTable tasks={clientTasks} showClient={false} showEdit={canEdit} />
        </>
      )}
      {tab === "invoices" && <InvoicesTable invoices={clientInvoices} />}
      {tab === "documents" && <DocumentHub client={client} canEdit={canEdit} tasks={clientTasks} submissions={submissions} setClients={setClients} showToast={showToast} />}
      {tab === "audit" && <AuditTrail clientId={client.id} />}
    </>
  );
}

function DocumentHub({ client, canEdit = true, tasks = [], submissions = [], setClients, showToast }) {
  const { user } = useApp();
  const staffMayUpload = canStaffUploadForClient(user?.role);
  const [files, setFiles]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingUploadFlag, setSavingUploadFlag] = useState(false);
  const fileRef = useRef(null);
  const prefix = client.id + "/";
  const uploadsActive = canClientUpload({ client, tasks, submissions });
  const uploadReason = client.uploadEnabled
    ? "manual"
    : clientHasRejectedDocs(tasks, submissions, client.id)
      ? "rejected"
      : clientHasRequiredUploadTasks(tasks, client.id)
        ? "required"
        : null;

  const toggleClientUpload = async () => {
    if (!canEdit || savingUploadFlag) return;
    const next = !client.uploadEnabled;
    setSavingUploadFlag(true);
    try {
      await sb.update("companies", "id", client.id, { upload_enabled: next });
      setClients(cs => cs.map(c => c.id === client.id ? { ...c, uploadEnabled: next } : c));
      showToast(next ? "Client uploading enabled" : "Client uploading disabled", "success");
    } catch {
      showToast("Could not update upload setting", "error");
    }
    setSavingUploadFlag(false);
  };

  const listFiles = async () => {
    try {
      setFiles(await listClientDocs(prefix));
    } catch (e) { setFiles([]); }
  };
  useEffect(() => { listFiles(); }, [client.id]);

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const safeName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,"_")}`;
      await uploadClientDoc(`${prefix}team/${safeName}`, file);
      showToast("File uploaded","success");
      listFiles();
    } catch (err) { showToast("Upload error", "error"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const shortName = (name) => name.replace(/^\d+_/, "");
  const clientSubs = (submissions || []).filter(s => s.clientId === client.id);

  return (
    <>
      <div className="card">
        <div className="card-head" style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <div className="card-title">Documents</div>
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, fontWeight:600, color: uploadsActive ? "var(--green)" : "var(--muted)" }}>
              Client uploads: {uploadsActive ? "enabled" : "disabled"}
              {uploadReason === "rejected" && " (reupload required)"}
              {uploadReason === "required" && " (action required)"}
              {uploadReason === "manual" && " (manual)"}
            </span>
            {canEdit && (
              <button
                className={`btn btn-sm ${client.uploadEnabled ? "" : "btn-primary"}`}
                style={client.uploadEnabled ? { background:"#FEF2F2", color:"var(--red)", border:"1px solid #FECACA" } : undefined}
                disabled={savingUploadFlag}
                onClick={toggleClientUpload}
              >
                {savingUploadFlag ? "Saving…" : client.uploadEnabled ? "Disable client uploading" : "Enable client uploading"}
              </button>
            )}
            <input ref={fileRef} type="file" style={{ display:"none" }} onChange={onUpload} />
            {staffMayUpload && (
              <button className="btn btn-sm btn-primary" onClick={()=>fileRef.current && fileRef.current.click()} disabled={uploading}>
                {uploading ? "Uploading…" : <InlineIcon name="upload" size={14} gap={5}>Upload for client</InlineIcon>}
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {files.length === 0 && <div style={{ fontSize:12, color:"var(--muted)" }}>No documents uploaded yet.</div>}
          {files.map(f => (
            <div key={f.name} className="row-item">
              <div style={{ width:32, height:32, borderRadius:8, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center" }}><Icon name="doc" size={16} /></div>
              <div style={{ flex:1, fontSize:13 }}>{shortName(f.name)}</div>
              <DocActions
                doc={{ path: f.path || `${prefix}${f.name}`, name: shortName(f.name) }}
                companyId={client.id}
                listPrefix={prefix}
                files={files}
                onError={(msg) => showToast(msg, "error")}
              />
            </div>
          ))}
        </div>
      </div>

      {clientSubs.length > 0 && (
        <div className="card" style={{ marginTop:16 }}>
          <div className="card-head"><div className="card-title">Submitted Form Data</div></div>
          <div className="card-body">
            {clientSubs.map(s => (
              <div key={s.id} style={{ marginBottom:14, paddingBottom:14, borderBottom:"1px solid #F3F4F6" }}>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:6 }}>{((tasks||[]).find(t=>t.id===s.taskId)||{}).title || "Submission"} · {s.status}</div>
                {Object.entries(s.formData || {}).map(([k, v]) => {
                  if (v && typeof v === 'object') return null;
                  return (
                    <div key={k} style={{ display:"flex", gap:10, fontSize:12, padding:"2px 0" }}>
                      <span style={{ color:"var(--muted)", minWidth:140 }}>{k}</span>
                      <span style={{ fontWeight:500 }}>{String(v)}</span>
                    </div>
                  );
                })}
                {s.documents && Object.entries(s.documents).map(([dirIdx, slots]) =>
                  Object.entries(slots || {}).map(([slotId, doc]) => doc ? (
                    <div key={`${s.id}-${dirIdx}-${slotId}`} className="row-item" style={{ marginTop:8, padding:"8px 0" }}>
                      <Icon name="doc" size={16} />
                      <div style={{ flex:1, fontSize:12 }}>{doc.name || slotId}</div>
                      <DocActions
                        doc={doc}
                        companyId={client.id}
                        taskId={s.taskId}
                        dirIdx={dirIdx}
                        slotId={slotId}
                        listPrefix={`${client.id}/`}
                        files={files}
                        onError={(msg) => showToast(msg, "error")}
                        compact
                      />
                    </div>
                  ) : null)
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function AuditTrail({ clientId }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    let on = true;
    sb.select("audit_log", `company_id=eq.${clientId}`).then(rows => {
      if (!on) return;
      setLogs((rows || []).filter(shouldPersistAudit));
    });
    return () => { on = false; };
  }, [clientId]);

  const ACTION_ICON = { failed: "warning", error: "warning", rejected: "close", denied: "close" };
  const logEvents = logs.map(l => ({
    date: l.created_at ? new Date(l.created_at).toLocaleString("en-IN",{ day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit" }) : "",
    ts:   l.created_at ? new Date(l.created_at).getTime() : 0,
    icon: ACTION_ICON[l.action?.split("_").pop()] || "warning",
    text: `${l.actor_name || "System"} — ${String(l.action||"").replace(/_/g," ")}${l.entity_label ? ` · ${l.entity_label}` : ""}${l.details ? ` · ${typeof l.details === "string" ? l.details : l.details.error || ""}` : ""}`,
    type: l.entity_type,
  }));

  const events = logEvents.sort((a, b) => b.ts - a.ts);

  return (
    <div className="card">
      <div className="card-head"><div className="card-title">Errors & Failures</div></div>
      <div>
        {events.length === 0 && <div className="empty"><div className="empty-icon"><Icon name="clipboard" size={30} /></div><div>No errors recorded for this client</div></div>}
        {events.map((e, i) => (
          <div key={i} className="row-item">
            <div style={{ width: 32, height: 32, borderRadius: 8, background: e.type === "task" ? "#F0FDF4" : e.type === "payment" ? "#F0FDF4" : "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{e.icon && <Icon name={e.icon} size={16} />}</div>
            <div style={{ flex: 1 }}><div style={{ fontSize: 13 }}>{e.text}</div></div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{e.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { ClientDetail, DocumentHub, AuditTrail };
